import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert, Badge, Button, DataTable, EmptyState, Grid, InfoButton, Inline, Link, PageHeader, Panel, ProgressBar, Segmented, Skeleton, Stack,
  StatusDot, t, Tag, Text, Timeline, toast,
} from '@kanbios/ui';
import { useEnvironmentFilter } from '../components/EnvironmentPicker';
import type { maintenance } from '@kanbios/api-client';
import { Shell } from '../components/Shell';
import { ServerStateLegend } from '../components/ServerState';
import { dotVariant } from '../components/StateColumn';
import { api, ApiFailure, unwrap } from '../lib/api';
import { clockTime, relativeTime } from '../lib/format';
import { useSession } from '../lib/session';
import { PendingButton } from '../components/PendingButton';
import { FULL_HEIGHT } from '../lib/tables';

type Decision = maintenance.components['schemas']['Decision'];
type Scope = 'mine' | 'team' | 'all';
const STATE_ORDER = ['incident', 'unknown', 'to_update', 'maintenance', 'ok'] as const;
const STATE_TONES = { ok: 'pos', to_update: 'warn', incident: 'neg', maintenance: 'info', unknown: 'neutral' } as const;

/**
 * Tableau de bord (SPEC-010): a queue of decisions, not a showcase of
 * indicators — what calls for an action, by horizon, each with its main
 * action, « Je prends » and « Demain »; the rest of the fleet in one line.
 */
/** SPEC-034 § 12.10, D94: a member of a project's team without a role of SPEC-001 lands on their projects. */
export function HomePage() {
  const { can } = useSession();
  return can('server.read') ? <DashboardPage /> : <Navigate to="/projects" replace />;
}

export function DashboardPage() {
  const { can, enums } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<Scope | null>(null);
  // D73: the global environment filter; the platform's own subjects stay.
  const env = useEnvironmentFilter();
  const [busy, setBusy] = useState<string | null>(null);
  const queue = useQuery({
    queryKey: ['decisions', scope, env],
    queryFn: async () => unwrap(await api.maintenance.GET('/decisions', { params: { query: scope ? { scope, env } : { env } } })),
    refetchInterval: 30_000,
  });
  const fleet = useQuery({ queryKey: ['fleet-summary', env], queryFn: async () => unwrap(await api.fleetState.GET('/summary', { params: { query: { scope: 'all', env } } })), refetchInterval: 60_000 });
  const data = queue.data;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['decisions'] });

  const take = async (d: Decision) => {
    if (!d.alertId) return;
    setBusy(d.id);
    try {
      unwrap(await api.fleetState.POST('/alerts/{alert}/claim', { params: { path: { alert: d.alertId } } }));
      toast({ tone: 'pos', title: t('dashboard.taken') });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: e.body.code === 'CONFLICT' ? 'info' : 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setBusy(null);
      refresh();
    }
  };
  const snooze = async (d: Decision) => {
    setBusy(d.id);
    try {
      const r = unwrap(await api.maintenance.POST('/decisions/{decision}/snooze', { params: { path: { decision: d.id } } }));
      toast({ tone: 'info', title: r.message });
    } catch (e) {
      if (e instanceof ApiFailure) toast({ tone: 'neg', title: e.body.message, description: e.body.hint });
    } finally {
      setBusy(null);
      refresh();
    }
  };

  // One line per decision: the subject with its context, the deadline as a bar, who holds it, its actions.
  const columns = [
    {
      key: 'title', label: t('dashboard.columns.subject'), grow: true, minWidth: 240,
      render: (d: Decision) => (
        <Inline gap={8} align="center" wrap={false}>
          <AppLink to={d.action.href}>{d.title}</AppLink>
          <Text variant="caption" tone="mute" truncate>{d.context}</Text>
        </Inline>
      ),
    },
    {
      key: 'dueAt', label: t('dashboard.columns.due'), width: 200,
      render: (d: Decision) => {
        if (!d.dueAt) return <Text variant="body-sm" tone="mute">{t('dashboard.noDue')}</Text>;
        const due = t('dashboard.dueAt', { time: clockTime(d.dueAt) });
        return d.elapsed !== null
          ? <ProgressBar size="md" value={Math.round(d.elapsed * 100)} label={due} ariaLabel={t('dashboard.elapsed')} />
          : <Text variant="body-sm">{due}</Text>;
      },
    },
    {
      // Nobody on it yet: the escalation countdown takes the place of « personne ».
      key: 'responsible', label: t('dashboard.columns.responsible'), width: 170,
      render: (d: Decision) => d.responsible
        ?? (d.escalationMinutes !== null
          ? <Badge tone="warn" size="sm">{t('dashboard.escalation', { n: d.escalationMinutes })}</Badge>
          : <Text tone="mute">{t('dashboard.nobody')}</Text>),
    },
    {
      key: 'actions', label: t('dashboard.columns.actions'), width: 300, sortable: false,
      render: (d: Decision) => (
        <Inline gap={4} align="center" wrap={false}>
          <Button variant="secondary" size="sm" onClick={() => navigate(d.action.href)}>{d.action.label}</Button>
          {can('alert.claim') && d.alertId && !d.responsible && <PendingButton variant="ghost" size="sm" pending={busy === d.id} onClick={() => void take(d)}>{t('dashboard.take')}</PendingButton>}
          {can('alert.claim') && d.canSnooze && d.horizon !== 'now' && <PendingButton variant="ghost" size="sm" pending={busy === d.id} onClick={() => void snooze(d)}>{t('dashboard.snooze')}</PendingButton>}
        </Inline>
      ),
    },
  ];

  const counts = fleet.data?.counts as Record<string, number> | undefined;
  return (
    <Shell crumbs={[{ label: t('nav.dashboard') }]}>
      <Stack gap={24}>
        <PageHeader
          title={t('dashboard.title')}
          subtitle={t('dashboard.subtitle')}
          actions={data && (
            <Segmented label={t('dashboard.scope')} size="sm" value={data.scope} onChange={id => setScope(id as Scope)}
              items={(['mine', 'team', 'all'] as const).map(id => ({ id, label: t(`dashboard.scopes.${id}`), count: data.counts[id] }))} />
          )}
        />
        {queue.error && <Alert tone="neg" title={t('dashboard.failed')} action={t('dashboard.retry')} onAction={() => void queue.refetch()} />}
        {data && (
          <Inline gap={8} align="center" wrap>
            <Text variant="body-sm">{t('dashboard.clients', { n: data.clients.concerned.length, total: data.clients.total })}</Text>
            {data.clients.concerned.slice(0, 8).map(c => <Tag key={c.name} onClick={() => navigate(`/clients/${c.name}`)}>{c.title}</Tag>)}
            <Text variant="body-sm" tone="mute">{t('dashboard.othersFine', { n: Math.max(0, data.clients.total - data.clients.concerned.length) })}</Text>
          </Inline>
        )}
        <Grid columns="2-1" gap={24}>
          <Stack gap={24}>
            {!data ? <Panel title={t('dashboard.horizon.now')}><Skeleton lines={5} /></Panel>
              : data.groups.length === 0 ? <Panel title={t('dashboard.queue')}><EmptyState icon="task_alt" title={t('dashboard.empty')} /></Panel>
                : data.groups.map(g => (
                  <Panel key={g.horizon} title={t(`dashboard.horizon.${g.horizon}`)} subtitle={t(`dashboard.horizonHint.${g.horizon}`)} flush>
                    <DataTable bordered={false} label={t(`dashboard.horizon.${g.horizon}`)} rows={g.items} rowKey="id" toolbar={false} columns={columns} height={FULL_HEIGHT} />
                  </Panel>
                ))}
          </Stack>
          <Stack gap={24}>
            <Panel title={t('dashboard.fleet')} actions={<AppLink to="/servers">{t('dashboard.explore')}</AppLink>}>
              <Stack gap={8}>
                <Inline gap={12} wrap>
                  {STATE_ORDER.map(s => {
                    const label = `${enums.states.find(x => x.value === s)?.label ?? s} ${counts?.[s] ?? '…'}`;
                    return (
                      <Inline key={s} gap={4} align="center">
                        <StatusDot tone={STATE_TONES[s]} variant={dotVariant(STATE_TONES[s])} label={label} />
                        <Text variant="body-sm" aria-hidden>{label}</Text>
                      </Inline>
                    );
                  })}
                </Inline>
                <Inline gap={4} align="center">
                  <Text variant="caption" tone="mute">{t('dashboard.fleetTotal', { n: fleet.data?.total ?? 0 })}</Text>
                  <InfoButton label={t('servers.legend')} title={t('servers.legendTitle')} size="sm"><ServerStateLegend /></InfoButton>
                </Inline>
              </Stack>
            </Panel>
            <Panel title={t('dashboard.inProgress')}>
              {data && data.inProgress.length
                ? <Stack gap={12}>{data.inProgress.map(d => (
                  <Stack key={d.id} gap={4}>
                    <AppLink to={d.action.href}>{d.title}</AppLink>
                    <Text variant="caption" tone="mute">{t('dashboard.heldBy', { name: d.responsible ?? '' })}</Text>
                  </Stack>
                ))}</Stack>
                : <Text tone="mute">{t('dashboard.nothingInProgress')}</Text>}
            </Panel>
            <Panel title={t('dashboard.later')}>
              {data && data.later.length
                ? <Timeline compact label={t('dashboard.later')} items={data.later.map(d => ({ id: d.id, title: d.title, when: relativeTime(d.snoozedUntil), status: 'upcoming' as const }))} />
                : <Text tone="mute">{t('dashboard.nothingLater')}</Text>}
            </Panel>
          </Stack>
        </Grid>
      </Stack>
    </Shell>
  );
}

/** A link inside the portal: a real href (keyboard, new tab), routed without reload. */
function AppLink({ to, children }: { to: string; children: ReactNode }) {
  const navigate = useNavigate();
  return <Link href={to} onClick={(e: { preventDefault(): void }) => { e.preventDefault(); navigate(to); }}>{children}</Link>;
}
